import type { AvailablePowerBIReports } from "../../stores/powerBIStore";
import { APIPROXY_HOST, STATICDATA_HOST } from "../environment";

type StoreRouteParams = {
   storeId: string;
};

type StoreDocumentRouteParams = StoreRouteParams & {
   documentId: string;
};

type EmptyRouteParams = Record<string, never>;

type CustomerRouteParams = {
   customerNumber: string;
};

type StoreCustomerRouteParams = StoreRouteParams & CustomerRouteParams;

type IdStoreCustomerRouteParams = StoreCustomerRouteParams & {
   id: string;
};

type CustomerOrderRouteParams = CustomerRouteParams & {
   orderNumber: string;
};

type CustomerPickupDatesRouteParams = CustomerRouteParams & {
   companyNumber: string;
};

type StaticDataRouteParams = {
   storeId: string;
   dataVersion: string;
};

type WebsocketParams = {
   protocol: "ws:" | "wss:";
};

type PowerBIReportRouteParams = StoreCustomerRouteParams & {
   reportName: AvailablePowerBIReports;
};

type M3CompanyCustomerRouteParams = CustomerRouteParams & {
   m3CompanyNumber: string;
};

type AttachmentRouteParams = {
   attachment: string;
};

type ContentfulEntriesRouteParams = {
   environment: string;
   spaceId: string;
};

// Base path of the apiproxy's Contentful route; the Contentful SDK appends /spaces/... to it
export const CONTENTFUL_PROXY_BASE_PATH = "/api/contentful";

type PlatformRouteMatch = ({ request }: { request: Request }) => boolean;

type PlatformRoute<TParams extends Record<string, string>> = {
   origin: "apiProxy" | "staticData";
   path: (params: TParams) => string;
   parsePath: (pathname: string) => TParams | null;
   match: (params?: Partial<TParams>) => PlatformRouteMatch;
};

const routeParameterPattern = /:([A-Za-z][A-Za-z0-9_]*)/g;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const createPathFromTemplate = <TParams extends Record<string, string>>(template: string, params: TParams) =>
   template.replace(routeParameterPattern, (_match, paramName: keyof TParams) => {
      const value = params[paramName];
      if (value === undefined) {
         throw new Error(`Missing route param "${String(paramName)}" for template "${template}"`);
      }
      return encodeURIComponent(value);
   });

const buildPattern = <TParams extends Record<string, string>>(
   template: string,
   segment: (name: keyof TParams) => string
): RegExp => {
   let pattern = "";
   let lastIndex = 0;

   for (const match of template.matchAll(routeParameterPattern)) {
      const [placeholder, paramName] = match as RegExpMatchArray & [string, keyof TParams];
      pattern += escapeRegExp(template.slice(lastIndex, match.index));
      pattern += segment(paramName);
      lastIndex = (match.index ?? 0) + placeholder.length;
   }

   pattern += escapeRegExp(template.slice(lastIndex));

   return new RegExp(`^${pattern}$`);
};

const createMatchPattern = <TParams extends Record<string, string>>(template: string, params: Partial<TParams> = {}) =>
   buildPattern<TParams>(template, (name) => {
      const value = params[name];
      return value === undefined ? "[^/]+" : escapeRegExp(encodeURIComponent(value));
   });

const createPathParamPattern = <TParams extends Record<string, string>>(template: string) =>
   buildPattern<TParams>(template, (name) => `(?<${String(name)}>[^/]+)`);

const decodePathParams = <TParams extends Record<string, string>>(groups: Record<string, string> = {}) =>
   Object.fromEntries(Object.entries(groups).map(([key, value]) => [key, decodeURIComponent(value)])) as TParams;

const createApiProxyRoute = <TParams extends Record<string, string>>(template: string): PlatformRoute<TParams> => {
   const pathParamPattern = createPathParamPattern<TParams>(template);
   return {
      origin: "apiProxy",
      path: (params) => createPathFromTemplate(template, params),
      parsePath: (pathname) => {
         const match = pathParamPattern.exec(pathname);
         return match === null ? null : decodePathParams<TParams>(match.groups);
      },
      match: (params) => {
         const pattern = createMatchPattern(template, params);
         return ({ request }) => pattern.test(new URL(request.url).pathname);
      }
   };
};

const createStaticDataRoute = <TParams extends Record<string, string>>(template: string): PlatformRoute<TParams> => ({
   ...createApiProxyRoute<TParams>(template),
   origin: "staticData"
});

export const platformRoutes = {
   complianceDocuments: createApiProxyRoute<StoreRouteParams>("/api/backend/sharepoint/compliance/:storeId/"),
   complianceDocumentContent: createApiProxyRoute<StoreDocumentRouteParams>(
      "/api/backend/sharepoint/compliance/:storeId/:documentId/content"
   ),
   customerSessionLogin: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/login"),
   customerSessionBootstrap: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/bootstrap"),
   customerSessionRefresh: createApiProxyRoute<EmptyRouteParams>("/api/backend/auth/refresh"),
   customerSessionCurrentUser: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/me"),
   customerSessionPassword: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/me/password"),
   customerSessionCompanyContact: createApiProxyRoute<StoreCustomerRouteParams>(
      "/api/backend/auth/:storeId/company/:customerNumber"
   ),
   customerSessionSmsRequestCode: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/sms/requestcode"),
   customerSessionSmsVerify: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/sms/verify"),
   customerSessionForgottenPassword: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/forgottenpassword"),
   customerSessionResetPassword: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/resetpassword"),
   customerSessionLogout: createApiProxyRoute<StoreRouteParams>("/api/backend/auth/:storeId/logout"),
   customerCompanyShippingAddress: createApiProxyRoute<StoreCustomerRouteParams>(
      "/api/backend/company/:storeId/:customerNumber/shippingaddress"
   ),
   legacyCustomerCart: createApiProxyRoute<CustomerRouteParams>("/api/backend/carts/:customerNumber"),
   customerCartStoreAware: createApiProxyRoute<StoreCustomerRouteParams>("/api/backend/carts/:storeId/:customerNumber"),
   customerEventIngest: createApiProxyRoute<CustomerRouteParams>("/api/events/:customerNumber"),
   customerFeatureAvailability: createApiProxyRoute<CustomerRouteParams>("/api/features/:customerNumber.json"),
   customerDeliveryDates: createApiProxyRoute<CustomerRouteParams>("/api/deliverydates_2022/:customerNumber"),
   customerPickupDates: createApiProxyRoute<CustomerPickupDatesRouteParams>(
      "/api/deliverydates_2022/pickupdates/:companyNumber/:customerNumber"
   ),
   productAvailability: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/productAvailability"),
   deliveryFeeInfo: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/deliveryFeeInfo"),
   customerAssortment: createApiProxyRoute<StoreRouteParams>("/api/assortment/tip/:storeId/API"),
   customerDeliveryNotes: createApiProxyRoute<CustomerOrderRouteParams>(
      "/api/invoices/customer/:customerNumber/deliverynote/:orderNumber"
   ),
   customerOrder: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/customerOrder"),
   customerDSPOrderPost: createApiProxyRoute("/api/backend/orders"),
   customerDSPOrder: createApiProxyRoute<{ id: string }>("/api/backend/orders/:id"),
   customerDSPOrderList: createApiProxyRoute("/api/backend/orders"),
   customerDSPOrderDetails: createApiProxyRoute<{ id: string }>("/api/backend/orders/:id"),
   customerOrderList: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/customerOrderList"),
   deliveryStatus: createApiProxyRoute<StoreCustomerRouteParams>(
      "/api/backend/delivery-status/:storeId/customers/:customerNumber/latest"
   ),
   deliveryWindow: createApiProxyRoute<CustomerRouteParams>("/api/deliverywindow/:customerNumber/delivery-windows"),
   subscriptionOrder: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/subscriptionOrder"),
   subscriptionOrderList: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/subscriptionOrderList"),
   changedSubscriptionDelivery: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/changedSubscriptionOrderDelivery"),
   subscriptionOrderStatus: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/subscriptionOrderStatus"),
   cancellationDate: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/cancellationDate"),
   serverEvent: createApiProxyRoute<WebsocketParams>("/api/backend/websocket/customer"),
   subscriptionPlannedDeliveryList: createApiProxyRoute<StoreRouteParams>("/api/tip/:storeId/API/customerOrderList"),
   staticDataHolidays: createStaticDataRoute<StaticDataRouteParams>("/:storeId/:dataVersion/holidays.json"),
   notificationRead: createApiProxyRoute<CustomerRouteParams>("/api/notifications/:customerNumber/read"),
   users: createApiProxyRoute<StoreCustomerRouteParams>("/api/backend/auth/:storeId/company/:customerNumber/users"),
   defaultUser: createApiProxyRoute<IdStoreCustomerRouteParams>(
      "/api/backend/auth/:storeId/company/:customerNumber/defaultuser/:id"
   ),
   favorites: createApiProxyRoute<StoreCustomerRouteParams>("/api/backend/favorites/:storeId/:customerNumber"),
   favorite: createApiProxyRoute<IdStoreCustomerRouteParams>("/api/backend/favorites/:storeId/:customerNumber/:id"),
   favoriteName: createApiProxyRoute<IdStoreCustomerRouteParams>("/api/backend/favorites/:storeId/:customerNumber/:id/name"),

   invoices: createApiProxyRoute<CustomerRouteParams>("/api/invoices/customer/:customerNumber"),
   invoicesForOrder: createApiProxyRoute<CustomerOrderRouteParams>("/api/invoices/customer/:customerNumber/order/:orderNumber"),
   powerBIReport: createApiProxyRoute<PowerBIReportRouteParams>("/api/backend/powerbi/:storeId/:reportName/:customerNumber"),
   availablePromotions: createApiProxyRoute<StoreCustomerRouteParams>(
      "/api/backend/discount/:storeId/:customerNumber/availablePromotions"
   ),
   promotionDiscounts: createApiProxyRoute<StoreCustomerRouteParams>(
      "/api/backend/discount/:storeId/:customerNumber/promotionDiscounts"
   ),
   promotionSignups: createApiProxyRoute<StoreCustomerRouteParams>("/api/backend/discount/:storeId/:customerNumber/signup"),
   promotionSignup: createApiProxyRoute<IdStoreCustomerRouteParams>("/api/backend/discount/:storeId/:customerNumber/signup/:id"),
   promotionEvent: createApiProxyRoute<StoreCustomerRouteParams>("/api/backend/discount/:storeId/:customerNumber/event"),
   recommendations: createApiProxyRoute<CustomerRouteParams>("/api/recommend/ml001/:customerNumber"),
   recommendationFeedback: createApiProxyRoute<CustomerRouteParams>("/api/recommend/:customerNumber"),
   recommendationsContractual: createApiProxyRoute<M3CompanyCustomerRouteParams>(
      "/api/recommend/contractual/:m3CompanyNumber/:customerNumber"
   ),
   claims: createApiProxyRoute<CustomerRouteParams>("/api/backend/claims/:customerNumber"),
   claimsList: createApiProxyRoute<CustomerRouteParams>("/api/backend/claims/list/:customerNumber"),
   claimsFile: createApiProxyRoute<EmptyRouteParams>("/api/backend/claims/file"),
   claimSubmit: createApiProxyRoute<EmptyRouteParams>("/api/backend/claims"),
   claimAttachment: createApiProxyRoute<AttachmentRouteParams>("/api/claims/attachment/:attachment"),
   contactForm: createApiProxyRoute<StoreRouteParams>("/api/:storeId/contactform"),
   notifications: createApiProxyRoute<CustomerRouteParams>("/api/notifications/:customerNumber"),
   notificationArchive: createApiProxyRoute<CustomerRouteParams>("/api/notifications/:customerNumber/archive"),
   notificationProfiles: createApiProxyRoute<CustomerRouteParams>("/api/notification_profiles/:customerNumber"),
   notificationContacts: createApiProxyRoute<CustomerRouteParams>("/api/notification_contacts/:customerNumber"),
   contentfulCachedEntries: createApiProxyRoute<ContentfulEntriesRouteParams>(
      `${CONTENTFUL_PROXY_BASE_PATH}/spaces/:spaceId/environments/:environment/entries`
   )
};

export const resolvePlatformRequestUrl = <TParams extends Record<string, string>>(
   route: PlatformRoute<TParams>,
   params: TParams
) => {
   const origin = route.origin === "staticData" ? STATICDATA_HOST : APIPROXY_HOST;
   if (!origin || origin === "undefined") {
      const hostName = route.origin === "staticData" ? "STATICDATA_HOST" : "APIPROXY_HOST";
      throw new Error(`Missing ${hostName} configuration for platform route origin "${route.origin}"`);
   }
   return `${origin}${route.path(params)}`;
};
